import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Button,
  TextField,
  Paper,
  Grid,
  Rating,
  Fade,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  IconButton
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SecurityIcon from '@mui/icons-material/Security';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import UserRatingStats from '../../components/UserRatingStats';
import { apiFetch, API_URL } from '../../config/api';

interface UserProfile {
  displayName: string;
  photoURL: string;
  location: string;
  rating: number;
  bio?: string;
  email?: string;
  phone?: string;
  emailVerified?: boolean;
  phoneVerified?: boolean;
  identityVerified?: boolean;
}

interface VerificationStatus {
  email: 'pending' | 'verified' | 'unverified';
  phone: 'pending' | 'verified' | 'unverified';
  identity: 'pending' | 'verified' | 'unverified';
}

type IdentityFlowStep = 0 | 1 | 2; // 0: contact, 1: code, 2: documents

interface DressItem {
  id: number;
  image: string;
  name: string;
  size: string;
  price: number; // Prix à la journée
  likes: number;
  liked: boolean;
  location: string;
}

// Plus de données mockées - tout sera chargé depuis Firestore

const ProfileTab: React.FC = () => {
  const { user } = useAuth();
  const [averageRating, setAverageRating] = useState<number | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  
  // Profil initial vide - sera chargé depuis Firestore
  const [profile, setProfile] = useState<UserProfile>({
    displayName: '',
    photoURL: '',
    location: '',
    rating: 0,
    bio: ''
  });

  // Charger le profil depuis Firestore
  useEffect(() => {
    const loadProfile = async () => {
      if (!user) {
        setLoadingProfile(false);
        return;
      }

      try {
        setLoadingProfile(true);
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setProfile({
            displayName: userData.displayName || userData.name || user.displayName || '',
            photoURL: userData.photoURL || userData.avatar || user.photoURL || '',
            location: userData.location || '',
            rating: 0, // Sera calculé depuis les vraies notes
            bio: userData.bio || ''
          });
        } else {
          // Utilisateur n'existe pas encore en base - utiliser les infos Firebase Auth
          setProfile({
            displayName: user.displayName || '',
            photoURL: user.photoURL || '',
            location: '',
            rating: 0,
            bio: ''
          });
        }
      } catch (error) {
        console.error('Erreur lors du chargement du profil:', error);
        // En cas d'erreur, utiliser les infos Firebase Auth
        setProfile({
          displayName: user?.displayName || '',
          photoURL: user?.photoURL || '',
          location: '',
          rating: 0,
          bio: ''
        });
      } finally {
        setLoadingProfile(false);
      }
    };

    loadProfile();
  }, [user]);
  
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    location: '',
    bio: ''
  });
  
  const [activeTab, setActiveTab] = useState<'dressing' | 'liked' | 'verification' | 'ratings'>('dressing');
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>({
    email: 'unverified',
    phone: 'unverified',
    identity: 'unverified'
  });
  const [openVerificationDialog, setOpenVerificationDialog] = useState(false);
  const [verificationData, setVerificationData] = useState({
    email: '',
    phone: '',
    identityDocument: null as File | null,
    selfie: null as File | null
  });
  
  // Charger le dressing depuis Firestore (vide par défaut)
  const [dressing, setDressing] = useState<DressItem[]>([]);
  const [likedDresses, setLikedDresses] = useState<DressItem[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Initialiser les données du formulaire avec le profil sauvegardé
  useEffect(() => {
          setFormData({
      displayName: profile.displayName,
      location: profile.location,
      bio: profile.bio || ''
    });
  }, [profile]);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérification de la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Le fichier est trop volumineux (max 5MB)');
      return;
    }

    // Vérification du type de fichier
    if (!file.type.startsWith('image/')) {
      setUploadError('Veuillez sélectionner une image');
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(false);
    setUploadProgress(0);

    try {
      // Simulation de l'upload avec progression
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        
        // Simulation de la progression
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 100) {
              clearInterval(progressInterval);
              setUploading(false);
              setUploadSuccess(true);
              const updatedProfile = { ...profile, photoURL: result };
              setProfile(updatedProfile);
              
              // Sauvegarde immédiate de la photo dans localStorage
              localStorage.setItem('userProfile', JSON.stringify(updatedProfile));
              
              setTimeout(() => setUploadSuccess(false), 3000);
              return 100;
            }
            return prev + 10;
          });
        }, 100);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      setUploadError('Erreur lors de l\'upload de l\'image');
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      // Sauvegarde du profil
      const updatedProfile = {
        ...profile,
        displayName: formData.displayName,
        location: formData.location,
        bio: formData.bio
      };
      
      setProfile(updatedProfile);
      
      // Sauvegarder dans Firestore
      const db = getFirestore();
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: formData.displayName,
        location: formData.location,
        bio: formData.bio,
        updatedAt: new Date()
      });
      
      setEditing(false);
      console.log('Profil sauvegardé dans Firestore:', updatedProfile);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du profil:', error);
    }
  };

  const handleCancel = () => {
    setFormData({
      displayName: profile.displayName,
      location: profile.location,
      bio: profile.bio || ''
    });
      setEditing(false);
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const toggleLike = (id: number) => {
    const updatedDressing = dressing.map((item: DressItem) => 
      item.id === id 
        ? { ...item, liked: !item.liked, likes: item.liked ? item.likes - 1 : item.likes + 1 }
        : item
    );
    
    setDressing(updatedDressing);
    
    // Sauvegarde dans localStorage
    localStorage.setItem('userDressing', JSON.stringify(updatedDressing));
  };

  // Fonctions pour la vérification d'identité
  const [identityFlowStep, setIdentityFlowStep] = useState<IdentityFlowStep>(0);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationSending, setVerificationSending] = useState(false);

  const handleIdentityVerification = () => {
    setOpenVerificationDialog(true);
    setIdentityFlowStep(0);
    setVerificationCode('');
    setVerificationError(null);
  };

  const handleVerificationFileUpload = (type: 'identityDocument' | 'selfie') => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setVerificationData(prev => ({ ...prev, [type]: file }));
    }
  };

  const handleVerificationSubmit = async () => {
    if (!user) return;
    setVerificationError(null);

    try {
      if (identityFlowStep === 0) {
        // Step 1: email + phone -> send code
        setVerificationSending(true);
        const response = await apiFetch('/api/identity/start', {
          method: 'POST',
          body: JSON.stringify({
            userId: user.uid,
            email: verificationData.email,
            phone: verificationData.phone,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success) {
          throw new Error(data?.error || 'Impossible d’envoyer le code');
        }
        setVerificationStatus(prev => ({ ...prev, email: 'pending', phone: 'pending', identity: 'unverified' }));
        setIdentityFlowStep(1);
        return;
      }

      if (identityFlowStep === 1) {
        // Step 2: confirm code
        setVerificationSending(true);
        const response = await apiFetch('/api/identity/confirm-code', {
          method: 'POST',
          body: JSON.stringify({
            userId: user.uid,
            code: verificationCode,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success) {
          throw new Error(data?.error || 'Code invalide');
        }
        setVerificationStatus(prev => ({ ...prev, email: 'verified', phone: 'verified' }));
        setIdentityFlowStep(2);
        return;
      }

      if (identityFlowStep === 2) {
        // Step 3: upload documents -> send to Allure inbox for manual review
        if (!verificationData.identityDocument || !verificationData.selfie) return;

        setVerificationSending(true);
        const form = new FormData();
        form.append('userId', user.uid);
        form.append('email', verificationData.email);
        form.append('phone', verificationData.phone);
        form.append('identityDocument', verificationData.identityDocument);
        form.append('selfie', verificationData.selfie);

        const response = await fetch(`${API_URL}/api/identity/submit-documents`, {
          method: 'POST',
          body: form,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success) {
          throw new Error(data?.error || 'Impossible d’envoyer les documents');
        }

        setVerificationStatus(prev => ({ ...prev, identity: 'pending' }));
        setOpenVerificationDialog(false);
      }
    } catch (e: any) {
      setVerificationError(e?.message || 'Erreur');
    } finally {
      setVerificationSending(false);
    }
  };

  const getVerificationChip = (status: 'pending' | 'verified' | 'unverified') => {
    switch (status) {
      case 'verified':
        return <Chip icon={<CheckCircleIcon />} label="Vérifié" color="success" size="small" />;
      case 'pending':
        return <Chip icon={<SecurityIcon />} label="En cours" color="warning" size="small" />;
      default:
        return <Chip icon={<CancelIcon />} label="Non vérifié" color="error" size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* En-tête du profil */}
      <Paper elevation={3} sx={{ p: 4, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={profile.photoURL}
                sx={{ width: 120, height: 120 }}
              />
              {editing && (
                <IconButton
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: '#a5816d',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#8B7355'
                    }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <PhotoCameraIcon />
                </IconButton>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </Box>
          </Grid>
          <Grid item xs>
            {editing ? (
              <Box>
                {/* Messages d'état pour l'upload */}
                {uploading && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Upload en cours...
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={uploadProgress} 
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {uploadProgress}%
                    </Typography>
                  </Box>
                )}
                
                {uploadError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {uploadError}
                  </Alert>
                )}
                
                {uploadSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Photo de profil mise à jour avec succès !
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Nom d'affichage"
                  value={formData.displayName}
                  onChange={handleInputChange('displayName')}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Localisation"
                  value={formData.location}
                  onChange={handleInputChange('location')}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Bio"
                  multiline
                  rows={3}
                  value={formData.bio}
                  onChange={handleInputChange('bio')}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
                  >
                    Sauvegarder
                  </Button>
                  <Button variant="outlined" onClick={handleCancel}>
                    Annuler
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box>
                {loadingProfile ? (
                  <Typography variant="body2" color="text.secondary">
                    Chargement du profil...
                  </Typography>
                ) : (
                  <>
                    <Typography variant="h4" gutterBottom>
                      {profile.displayName || user?.displayName || user?.email || 'Utilisateur'}
                    </Typography>
                    {profile.location && (
                      <Typography variant="body1" color="text.secondary" gutterBottom>
                        📍 {profile.location}
                      </Typography>
                    )}
                    {averageRating !== null && averageRating > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Rating value={averageRating} readOnly precision={0.1} />
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          ({averageRating.toFixed(1)}/5)
                        </Typography>
                      </Box>
                    )}
                    {profile.bio && (
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {profile.bio}
                      </Typography>
                    )}
                  </>
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                    startIcon={<CheckroomIcon />}
                    onClick={() => navigate('/add-dress')}
                    sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
                  >
                    Ajouter une robe
                  </Button>
                  <Button 
                    variant="outlined" 
                  onClick={() => setEditing(true)}
                    sx={{ borderColor: '#a5816d', color: '#a5816d' }}
                >
                  Modifier le profil
                </Button>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Onglets */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
          <Button
          onClick={() => setActiveTab('dressing')}
          sx={{
              flex: 1,
              py: 2,
              backgroundColor: activeTab === 'dressing' ? '#a5816d' : 'transparent',
              color: activeTab === 'dressing' ? 'white' : 'inherit',
              '&:hover': {
                backgroundColor: activeTab === 'dressing' ? '#8B7355' : '#f5f5f5'
              }
            }}
          >
            Mon Dressing ({dressing.length})
          </Button>
          <Button
          onClick={() => setActiveTab('liked')}
          sx={{
              flex: 1,
              py: 2,
              backgroundColor: activeTab === 'liked' ? '#a5816d' : 'transparent',
              color: activeTab === 'liked' ? 'white' : 'inherit',
              '&:hover': {
                backgroundColor: activeTab === 'liked' ? '#8B7355' : '#f5f5f5'
              }
            }}
          >
            Robes Likées ({likedDresses.length})
          </Button>
          <Button
            onClick={() => setActiveTab('ratings')}
            sx={{
              flex: 1,
              py: 2,
              backgroundColor: activeTab === 'ratings' ? '#a5816d' : 'transparent',
              color: activeTab === 'ratings' ? 'white' : 'inherit',
              '&:hover': {
                backgroundColor: activeTab === 'ratings' ? '#8B7355' : '#f5f5f5'
              }
            }}
          >
            Notes et avis
          </Button>
            <Button
            onClick={() => setActiveTab('verification')}
              sx={{
              flex: 1,
              py: 2,
              backgroundColor: activeTab === 'verification' ? '#a5816d' : 'transparent',
              color: activeTab === 'verification' ? 'white' : 'inherit',
              '&:hover': {
                backgroundColor: activeTab === 'verification' ? '#8B7355' : '#f5f5f5'
              }
            }}
          >
            Vérification d'identité
            </Button>
          </Box>
      </Paper>

      {/* Contenu des onglets */}
      <Fade in={activeTab === 'dressing'} timeout={300}>
        <Box sx={{ display: activeTab === 'dressing' ? 'block' : 'none' }}>
          {dressing.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Aucune robe dans votre dressing pour le moment
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/dashboard/add-dress')}
                sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
              >
                Ajouter ma première robe
              </Button>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {dressing.map((item: DressItem) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Paper 
                  elevation={2} 
                  sx={{
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    }
                  }}
                  onClick={() => {
                    console.log('Clic sur la robe du dressing:', item.id);
                    navigate(`/dashboard/dress/${item.id}`);
                  }}
                >
                  <Box sx={{ height: 200, backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Image: {item.name}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    {/* Nom de la robe */}
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {item.name}
                    </Typography>
                    
                    {/* Prix à la journée */}
                    <Typography variant="h6" gutterBottom sx={{ color: '#a5816d', fontWeight: 'bold' }}>
                      {item.price}€ / jour
                    </Typography>
                    
                    {/* Taille */}
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Taille: {item.size}
                    </Typography>
                    
                    {/* Localisation */}
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      📍 {item.location}
                    </Typography>
                    
                    {/* Indication de clic */}
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontWeight: 'bold', 
                      color: '#a5816d',
                      textDecoration: 'underline',
                      mb: 1
                    }}>
                      👆 Cliquez pour voir les détails
                    </Typography>
                    
                    {/* Likes et bouton like */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        {item.likes} likes
                      </Typography>
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation(); // Empêche la navigation quand on clique sur le cœur
                          toggleLike(item.id);
                        }}
                        color={item.liked ? 'error' : 'default'}
                        size="small"
                      >
                        {item.liked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Fade>

      <Fade in={activeTab === 'liked'} timeout={300}>
        <Box sx={{ display: activeTab === 'liked' ? 'block' : 'none' }}>
          <Grid container spacing={3}>
            {likedDresses.length === 0 ? (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Aucune robe likée pour le moment
                  </Typography>
                </Box>
              </Grid>
            ) : (
              likedDresses.map((item: DressItem) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                  overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                    }
                  }}
                  onClick={() => {
                    console.log('Clic sur la robe aimée:', item.id);
                    navigate(`/dashboard/dress/${item.id}`);
                  }}
                >
                  <Box sx={{ height: 200, backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Image: {item.name}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    {/* Nom de la robe */}
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                      {item.name}
                    </Typography>
                    
                    {/* Prix à la journée */}
                    <Typography variant="h6" gutterBottom sx={{ color: '#a5816d', fontWeight: 'bold' }}>
                      {item.price}€ / jour
                    </Typography>
                    
                    {/* Taille */}
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Taille: {item.size}
                    </Typography>
                    
                    {/* Localisation */}
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      📍 {item.location}
                    </Typography>
                    
                    {/* Indication de clic */}
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      fontWeight: 'bold', 
                      color: '#a5816d',
                      textDecoration: 'underline',
                      mb: 1
                    }}>
                      👆 Cliquez pour voir les détails
                    </Typography>
                    
                    {/* Likes */}
                    <Typography variant="body2" color="text.secondary">
                      {item.likes} likes
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
              ))
            )}
          </Grid>
        </Box>
      </Fade>

      {/* Onglet Notes et avis */}
      <Fade in={activeTab === 'ratings'} timeout={300}>
        <Box sx={{ display: activeTab === 'ratings' ? 'block' : 'none' }}>
          {user ? (
            <UserRatingStats 
              userId={user.uid} 
              userType="owner" 
              title="Mes notes et avis reçus"
            />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Veuillez vous connecter pour voir vos notes.
              </Typography>
            </Box>
          )}
        </Box>
      </Fade>

      {/* Onglet Vérification d'identité */}
      <Fade in={activeTab === 'verification'} timeout={300}>
        <Box sx={{ display: activeTab === 'verification' ? 'block' : 'none' }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#a5816d', mb: 3 }}>
            <VerifiedUserIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Vérification d'identité
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Pour votre sécurité et celle de notre communauté, nous vous demandons de vérifier votre identité. 
            Cette vérification est obligatoire pour louer des robes.
          </Typography>

          <Grid container spacing={3}>
            {/* Vérification Email */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EmailIcon sx={{ mr: 1, color: '#a5816d' }} />
                    <Typography variant="h6">Vérification E-mail</Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    {getVerificationChip(verificationStatus.email)}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Nous enverrons un lien de vérification à votre adresse e-mail.
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Adresse e-mail"
                    type="email"
                    value={verificationData.email}
                    onChange={(e) => setVerificationData(prev => ({ ...prev, email: e.target.value }))}
                    sx={{ mb: 2 }}
                  />
                  
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleIdentityVerification}
                    disabled={!verificationData.email}
                    sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
                  >
                    Vérifier l'e-mail
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Vérification Téléphone */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PhoneIcon sx={{ mr: 1, color: '#a5816d' }} />
                    <Typography variant="h6">Vérification Téléphone</Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    {getVerificationChip(verificationStatus.phone)}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Nous enverrons un code de vérification par SMS.
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Numéro de téléphone"
                    type="tel"
                    value={verificationData.phone}
                    onChange={(e) => setVerificationData(prev => ({ ...prev, phone: e.target.value }))}
                    sx={{ mb: 2 }}
                  />
                  
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleIdentityVerification}
                    disabled={!verificationData.phone}
                    sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
                  >
                    Vérifier le téléphone
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Vérification Identité */}
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SecurityIcon sx={{ mr: 1, color: '#a5816d' }} />
                    <Typography variant="h6">Vérification d'identité</Typography>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    {getVerificationChip(verificationStatus.identity)}
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Photo d'identité + selfie pour vérifier votre identité.
                  </Typography>
                  
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleIdentityVerification}
                    disabled={verificationStatus.identity === 'pending'}
                    sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
                  >
                    {verificationStatus.identity === 'pending' ? 'Vérification en cours...' : 'Vérifier l\'identité'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Statut global */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statut de vérification global
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {verificationStatus.email === 'verified' && verificationStatus.phone === 'verified' && verificationStatus.identity === 'verified' ? (
                  <>
                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                    <Typography variant="body1" color="success.main" sx={{ fontWeight: 'bold' }}>
                      Identité complètement vérifiée
                    </Typography>
                  </>
                ) : (
                  <>
                    <SecurityIcon sx={{ color: 'warning.main' }} />
                    <Typography variant="body1" color="warning.main" sx={{ fontWeight: 'bold' }}>
                      Vérification incomplète
                    </Typography>
                  </>
                )}
              </Box>
              
              <Typography variant="body2" color="text.secondary">
                Une vérification complète est nécessaire pour accéder à toutes les fonctionnalités de location.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Fade>

      {/* Dialog de vérification d'identité */}
      <Dialog 
        open={openVerificationDialog} 
        onClose={() => setOpenVerificationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1, color: '#a5816d' }} />
            Vérification d'identité
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={identityFlowStep} sx={{ mb: 3 }}>
            <Step>
              <StepLabel>Contact</StepLabel>
            </Step>
            <Step>
              <StepLabel>Code</StepLabel>
            </Step>
            <Step>
              <StepLabel>Documents</StepLabel>
            </Step>
          </Stepper>

          {verificationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {verificationError}
            </Alert>
          )}

          {identityFlowStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Étape 1: Email & téléphone
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Nous vous envoyons un <strong>code</strong> sur votre e-mail. Une fois le code validé, vous pourrez envoyer vos pièces d’identité.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Adresse e-mail"
                    type="email"
                    value={verificationData.email}
                    onChange={(e) => setVerificationData(prev => ({ ...prev, email: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Numéro de téléphone"
                    type="tel"
                    value={verificationData.phone}
                    onChange={(e) => setVerificationData(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {identityFlowStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Étape 2: Code reçu
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Entrez le code à 6 chiffres reçu par e-mail.
              </Typography>

              <TextField
                fullWidth
                label="Code"
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\\D/g, '').slice(0, 6))}
              />
            </Box>
          )}

          {identityFlowStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Étape 3: Documents d'identité
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Vos documents seront envoyés sur la boîte mail Allure pour vérification manuelle.
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Pièce d'identité
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Carte d'identité, passeport ou permis de conduire
                      </Typography>

                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleVerificationFileUpload('identityDocument')}
                        style={{ display: 'none' }}
                        id="identity-document-upload"
                      />
                      <label htmlFor="identity-document-upload">
                        <Button variant="outlined" component="span" startIcon={<PhotoCameraIcon />} fullWidth>
                          {verificationData.identityDocument ? 'Document sélectionné' : 'Sélectionner un document'}
                        </Button>
                      </label>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Selfie
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Photo de vous tenant votre pièce d'identité
                      </Typography>

                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleVerificationFileUpload('selfie')}
                        style={{ display: 'none' }}
                        id="selfie-upload"
                      />
                      <label htmlFor="selfie-upload">
                        <Button variant="outlined" component="span" startIcon={<PhotoCameraIcon />} fullWidth>
                          {verificationData.selfie ? 'Selfie sélectionné' : 'Prendre un selfie'}
                        </Button>
                      </label>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpenVerificationDialog(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleVerificationSubmit}
            variant="contained"
            disabled={verificationSending || (
              identityFlowStep === 0 ? (!verificationData.email || !verificationData.phone) :
              identityFlowStep === 1 ? (verificationCode.length !== 6) :
              identityFlowStep === 2 ? (!verificationData.identityDocument || !verificationData.selfie) :
              true
            )}
            sx={{ backgroundColor: '#a5816d', '&:hover': { backgroundColor: '#8B7355' } }}
          >
            {verificationSending
              ? 'En cours...'
              : identityFlowStep === 0
                ? 'Envoyer le code'
                : identityFlowStep === 1
                  ? 'Valider le code'
                  : 'Envoyer les documents'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileTab; 